import type { DroppableId } from "@web/src/app/use-cases/id-flavors";
import type { DndStore, DndTypeBundle} from "@web/src/hooks/dnd/dnd-store";
import { DroppableGroupContext } from "@web/src/hooks/dnd/dnd-store";
import React, { useEffect, useMemo } from "react";

/**
 * Registers an element as a droppable target
 * @param droppableId the id for this droppable
 * @param ref the ref to the container representing this droppable target
 */
export function useDroppable<TDndTypeBundle extends DndTypeBundle>(
  droppableId: DroppableId,
  droppableData: Omit<TDndTypeBundle["TDroppableData"], "groupId" | "droppableId" | "canDrop">,
  ref: React.MutableRefObject<HTMLDivElement | null>,
  dndStore: DndStore<TDndTypeBundle>,
  canDrop?: () => boolean
) {
  const { droppableGroupId } = React.useContext(DroppableGroupContext);
  const { droppableInsertPosition, droppableTargetId } = dndStore;

  useEffect(() => {
    dndStore.registerDroppable(droppableId, { ...droppableData, droppableId, groupId: droppableGroupId, canDrop }, ref);
    return () => {
      dndStore.deregisterDroppable(droppableId);
    };
  }, [dndStore, droppableId, droppableGroupId, ref, droppableData, canDrop]);

  const isHoverDrop = droppableTargetId === droppableId && droppableInsertPosition;

  const containerProps = useMemo(() => {
    return {
      /**
       * Note: have to use react synthetic events because there is a stopPropagation in useTodoDraggable
       */
      onMouseLeave: () => {
        if (isHoverDrop) {
          dndStore.clearDroppableTarget();
        }
      },
    };
  }, [dndStore, isHoverDrop]);

  return {
    containerProps,
  };
}
